import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, Field, SubmissionError } from 'redux-form';

import { scrollToElement } from '@sparefoot/react-core/utils/scrolling';

import { Paragraph, H4 } from 'components/core/Typography';
import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import {
	Form,
	FormItem,
	ReduxFormInput,
	ReduxTextarea,
	ReduxCheckbox,
	ReduxRadioGroup,
	ReduxStarInput,
	ReduxSliderInput,
} from 'components/core/Form';
import { LoadingMask } from 'components/core/LoadingMask';
import { RadioInput } from 'components/core/RadioInput';

import validate from './ReviewFormValidation';

import './ReviewForm.scss';

const defaultInitialValues = {
	rating_price: 3,
	rating_paperwork: 3,
	rating_service: 3,
	rating_cleanliness: 3,
};

export const enhance = reduxForm({
	form: 'facility-review',
	validate,
});

export function ReviewForm({
	error,
	handleSubmit,
	invalid,
	submitting,
	submitSucceeded,
	initialValues = defaultInitialValues,
	facilityId,
	source,
	onSubmit: onSubmitProp,
}) {
	const onSubmit = useCallback(
		(formData) =>
			onSubmitProp({
				...formData,
				source,
				facilityId,
			}).then((response) => {
				if (response.error) {
					throw new SubmissionError({
						_error: response.error.message,
					});
				} else {
					setTimeout(() => {
						scrollToElement('.review-form', {
							behavior: 'smooth',
						});
					}, 300);
					return response;
				}
			}),
		[onSubmitProp, source, facilityId],
	);

	return (
		<Form
			className="review-form"
			onSubmit={handleSubmit(onSubmit)}
			invalid={invalid}
			successLabel="success-review-form"
			failureLabel="fail-review-form"
		>
			<div className="review-form-content">
				{!submitSucceeded && (
					<div className="review-form-fields">
						<H4
							size="small"
							color="red"
						>
							Rate and review
						</H4>
						<div className="ratings">
							<FormItem>
								<Field
									name="rating"
									id="rating"
									label="Overall rating (1-5 stars)"
									halfStep
									size="large"
									helpTexts={[
										'Poor',
										'Fair',
										'Average',
										'Great',
										'Best',
									]}
									component={ReduxStarInput}
								/>
							</FormItem>
							<FormItem>
								<Field
									name="rating_price"
									id="rating_price"
									label="Pricing"
									defaultValue={initialValues.rating_price}
									step={1}
									min={1}
									max={5}
									helpTexts={[
										'Unfair price',
										'A little steep',
										'Fair price',
										'Good value',
										'Excellent value',
									]}
									component={ReduxSliderInput}
								/>
							</FormItem>
							<FormItem>
								<Field
									name="rating_paperwork"
									id="rating_paperwork"
									label="Time on paperwork"
									defaultValue={
										initialValues.rating_paperwork
									}
									step={1}
									min={1}
									max={5}
									helpTexts={[
										'Over 35 minutes',
										'About 30 minutes',
										'About 20 minutes',
										'About 15 minutes',
										'Under 10 minutes',
									]}
									component={ReduxSliderInput}
								/>
							</FormItem>
							<FormItem>
								<Field
									name="rating_service"
									id="rating_service"
									label="Customer service"
									defaultValue={initialValues.rating_service}
									step={1}
									min={1}
									max={5}
									helpTexts={[
										'Very unhelpful',
										'Somewhat unhelpful',
										'Helpful',
										'Very helpful',
										'Outstanding',
									]}
									component={ReduxSliderInput}
								/>
							</FormItem>
							<FormItem>
								<Field
									name="rating_cleanliness"
									id="rating_cleanliness"
									label="Cleanliness"
									defaultValue={
										initialValues.rating_cleanliness
									}
									step={1}
									min={1}
									max={5}
									helpTexts={[
										'Very dirty',
										'Somewhat dirty',
										'Average',
										'Clean',
										'Squeaky clean',
									]}
									component={ReduxSliderInput}
								/>
							</FormItem>
							<FormItem>
								<Field
									name="rating_recommended"
									label="Would you recommend this facility to a friend?"
									component={ReduxRadioGroup}
								>
									<RadioInput
										component={RadioInput}
										label="Yes"
										value="1"
									/>
									<RadioInput
										label="No"
										value="0"
									/>
								</Field>
							</FormItem>
						</div>
						<H4
							size="small"
							color="red"
						>
							Tell Your Storage Story
						</H4>
						<FormItem>
							<Field
								label="One-line summary"
								name="title"
								id="title"
								component={ReduxFormInput}
								errorMessage="Please provide a short summary"
							/>
						</FormItem>
						<FormItem>
							<Field
								label="Review"
								name="message"
								id="message"
								component={ReduxTextarea}
								errorMessage="Please provide a review with at least 30 characters"
							/>
						</FormItem>
						<FormItem>
							<Field
								label="Your name"
								name="nickname"
								id="nickname"
								component={ReduxFormInput}
								errorMessage="Please provide your name"
							/>
						</FormItem>
						<FormItem>
							<Field
								label="Your email address"
								name="email"
								id="email"
								component={ReduxFormInput}
								errorMessage="Please provide your email"
							/>
						</FormItem>
						<FormItem>
							<div className="disclaimer">
								<Field
									name="review_tos"
									id="review_tos"
									component={ReduxCheckbox}
									errorMessage="Please accept the terms of service"
									label={
										<span>
											By submitting this review, I
											acknowledge and agree to the{' '}
											<Link
												secondary
												href="/legal/reviews"
												rel="noopener noreferrer"
												target="_blank"
											>
												Review Terms and Conditions
											</Link>
											. In doing so, I certify that this
											review reflects my honest opinion
											and is based on a real experience
											with the facility. I further certify
											that I have no relationship with
											this facility, and have not been
											offered any compensation by the
											facility to write this review. I
											understand that submitting a
											fraudulent review is a serious
											violation of our Review Terms and
											Conditions and may be prohibited by
											law in your jurisdiction.{' '}
											<Link
												secondary
												href="https://ftc.gov/os/2009/10/091005revisedendorsementguides.pdf"
												rel="noopener noreferrer"
												target="_blank"
											>
												Learn more.
											</Link>
										</span>
									}
								/>
							</div>
						</FormItem>
						{!!error && (
							<Paragraph className="alert error-message">
								{error}
							</Paragraph>
						)}
						<div className="form-submit">
							<Button
								primary
								fullWidth
								type="submit"
								disabled={submitting}
								className="submit-button"
							>
								Submit review
							</Button>
						</div>
					</div>
				)}
				{!!submitSucceeded && (
					<div className="success-message">
						Thanks for giving your feedback! Please allow up to 5
						business days for your review to be approved and
						published.
					</div>
				)}
			</div>
			{submitting && <LoadingMask />}
		</Form>
	);
}

ReviewForm.propTypes = {
	error: PropTypes.string,
	handleSubmit: PropTypes.func,
	invalid: PropTypes.bool,
	submitting: PropTypes.bool,
	submitSucceeded: PropTypes.bool,
	initialValues: PropTypes.object,
	facilityId: PropTypes.number,
	source: PropTypes.string,
	onSubmit: PropTypes.func,
};

export default enhance(ReviewForm);
